import {
  UserCheck, Eye, CheckCircle, AlertTriangle, Clock,
  ArrowRight, Shield, Search, Filter, Activity,
  TrendingUp, TrendingDown, Calendar, Target
} from "lucide-react";
import { Button } from "../../../../../ui/common-ui/button";
import { Input } from "../../../../../ui/common-ui/input";

interface HumanData {
  detected: number;
  verified: number;
  reviewing: number;
  topNhiForReview: { name: string; riskScore: number; reason: string }[];
  complianceRatePct?: number;
  reviewStats?: any[];
  reviewQueue?: any[];
  recentNhiActivity?: any[];
}

interface ExpandedHumanCardProps {
  data: HumanData;
}

export const ExpandedHumanCard = ({ data }: ExpandedHumanCardProps) => {
  const { detected, verified, reviewing } = data;

  const nhisUnderReview = data.reviewQueue?.length ? data.reviewQueue : [
    { name: "svc-legacy-api", riskScore: 92, reason: "No owner, aged 180+ days", status: "critical", assignee: "john@company.com", dueDate: "Today" },
    { name: "ci-prod-deploy", riskScore: 87, reason: "Overprivileged access", status: "high", assignee: "sarah@company.com", dueDate: "Tomorrow" },
    { name: "backup-automation", riskScore: 78, reason: "Stale credentials", status: "medium", assignee: "Unassigned", dueDate: "This week" },
    { name: "analytics-ingestion", riskScore: 72, reason: "Unusual access patterns", status: "medium", assignee: "mike@company.com", dueDate: "This week" },
    { name: "notification-svc", riskScore: 65, reason: "Missing MFA", status: "low", assignee: "Unassigned", dueDate: "Next week" },
  ];

  const recentReviews = [
    { nhi: "prod-db-admin", reviewer: "alice@company.com", action: "Approved", time: "2 hours ago", notes: "Permissions validated" },
    { nhi: "api-gateway-key", reviewer: "bob@company.com", action: "Flagged", time: "5 hours ago", notes: "Needs owner assignment" },
    { nhi: "logging-service", reviewer: "carol@company.com", action: "Approved", time: "1 day ago", notes: "Rotation scheduled" },
    { nhi: "cache-worker", reviewer: "dave@company.com", action: "Revoked", time: "2 days ago", notes: "No longer needed" },
  ];

  const reviewStats = data.reviewStats?.length ? data.reviewStats : [
    { label: "Avg Review Time", value: "2.3 days", trend: "down", delta: "-0.5" },
    { label: "Reviews This Week", value: "47", trend: "up", delta: "+12" },
    { label: "Pending Reviews", value: String(reviewing), trend: "down", delta: "-8" },
    { label: "Compliance Rate", value: "94%", trend: "up", delta: "+3%" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-sky-500/10">
          <UserCheck className="w-4 h-4 text-sky-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-semibold text-[15px] text-foreground">NHI Review</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-6 text-[11px]">
                <Calendar className="w-3 h-3 mr-1" />
                Schedule Review
              </Button>
              <Button size="sm" className="h-6 text-[11px]">
                <Target className="w-3 h-3 mr-1" />
                Start Review
              </Button>
            </div>
          </div>
          <p className="text-[13px] text-muted-foreground">Review and audit non-human identity usage patterns</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-5 h-5 text-primary" />
            <div className="px-1.5 py-0.5 rounded-full bg-primary/20">
              <span className="text-[11px] font-bold text-primary">Urgent</span>
            </div>
          </div>
          <div className="text-2xl font-bold text-primary">{detected}</div>
          <p className="text-[13px] text-muted-foreground">Detected Issues</p>
        </div>

        <div className="p-4 rounded-xl bg-success/10 border border-success/20">
          <CheckCircle className="w-5 h-5 text-success mb-2" />
          <div className="text-2xl font-bold text-success">{verified.toLocaleString()}</div>
          <p className="text-[13px] text-muted-foreground">Verified NHIs</p>
        </div>

        <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20">
          <Eye className="w-5 h-5 text-sky-500 mb-2" />
          <div className="text-2xl font-bold text-sky-500">{reviewing}</div>
          <p className="text-[13px] text-muted-foreground">Under Review</p>
        </div>

        <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20">
          <Activity className="w-5 h-5 text-sky-500 mb-2" />
          <div className="text-2xl font-bold text-sky-500">94%</div>
          <p className="text-[13px] text-muted-foreground">Compliance Rate</p>
        </div>
      </div>

      {/* Review Stats */}
      <div className="grid grid-cols-4 gap-3">
        {reviewStats.map((stat) => (
          <div
            key={stat.label}
            className="p-3 rounded-lg bg-muted/20 border border-border/30"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-muted-foreground">{stat.label}</span>
              <div className={`flex items-center gap-0.5 text-[11px] font-medium ${stat.trend === "up" ? "text-success" : "text-primary"
                }`}>
                {stat.trend === "up" ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {stat.delta}
              </div>
            </div>
            <div className="text-lg font-bold text-foreground">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* NHIs Requiring Review */}
      <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-sky-500" />
            NHIs Requiring Immediate Review
          </h3>
          <div className="flex gap-2">
            <div className="relative w-48">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input placeholder="Search NHIs..." className="pl-7 h-7 text-[13px]" />
            </div>
            <Button variant="outline" size="sm" className="h-7 text-[13px]">
              <Filter className="w-3 h-3 mr-1" />
              Filter
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border/50">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">NHI Name</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Reason</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Assignee</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Due Date</th>
                <th className="text-left p-2 text-[11px] font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {nhisUnderReview.map((nhi) => (
                <tr
                  key={nhi.name}
                  className="border-t border-border/30 hover:bg-muted/30 transition-colors"
                >
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${nhi.status === "critical" ? "bg-primary" :
                          nhi.status === "high" ? "bg-primary" :
                            nhi.status === "medium" ? "bg-sky-500" : "bg-success"
                        }`} />
                      <span className="font-medium text-[13px]">{nhi.name}</span>
                    </div>
                  </td>
                  <td className="p-2 text-[13px] text-muted-foreground">{nhi.reason}</td>
                  <td className="p-2">
                    <span className={nhi.assignee === "Unassigned" ? "text-primary text-[13px]" : "text-[13px]"}>
                      {nhi.assignee}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${nhi.dueDate === "Today" ? "bg-primary/10 text-primary" : "bg-sky-500/10 text-sky-500"
                      }`}>{nhi.dueDate}</span>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-6 text-[11px]">
                        <Eye className="w-3 h-3 mr-1" />
                        Review
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Reviews */}
      <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-sky-500" />
            Recent Review Activity
          </h3>
          <Button variant="ghost" size="sm" className="h-6 text-[13px]">
            View All
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {recentReviews.map((review, i) => (
            <div
              key={`${review.nhi}-${i}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-card hover:bg-muted/30 transition-all"
            >
              <div className={`p-2 rounded-lg ${review.action === "Approved" ? "bg-success/10" :
                  review.action === "Flagged" ? "bg-sky-500/10" : "bg-primary/10"
                }`}>
                {review.action === "Approved" && <CheckCircle className="w-4 h-4 text-success" />}
                {review.action === "Flagged" && <AlertTriangle className="w-4 h-4 text-sky-500" />}
                {review.action === "Revoked" && <Shield className="w-4 h-4 text-primary" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-[13px]">{review.nhi}</span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${review.action === "Approved" ? "bg-success/10 text-success" :
                      review.action === "Flagged" ? "bg-sky-500/10 text-sky-500" : "bg-primary/10 text-primary"
                    }`}>{review.action}</span>
                </div>
                <div className="text-[11px] text-muted-foreground">{review.notes}</div>
                <div className="text-[11px] text-muted-foreground">
                  by {review.reviewer} • {review.time}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
